import { motion } from "framer-motion";
import {
  BookOpen,
  ClipboardCheck,
  FileText,
  CreditCard,
  BarChart3,
  Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";

const EASE = [0.32, 0.72, 0, 1] as const;

// ============ Mini mockups ============

function MockupClasses() {
  return (
    <div className="rounded-xl bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="h-1.5 w-16 rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="ml-auto h-5 w-16 rounded-md bg-blue-600/80" />
      </div>
      {[
        { name: "Lớp 10A — Toán", students: "28", color: "bg-blue-500" },
        { name: "Lớp 11B — Lý", students: "24", color: "bg-emerald-500" },
        { name: "Lớp 12C — Hoá", students: "30", color: "bg-violet-500" },
        { name: "IELTS — Anh", students: "15", color: "bg-amber-500" },
      ].map((c, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
          <div className={`size-1.5 rounded-full ${c.color}`} />
          <span className="text-[10px] font-medium text-slate-700 dark:text-slate-200 flex-1 truncate">{c.name}</span>
          <span className="text-[9px] text-slate-400 dark:text-slate-500">{c.students} HS</span>
        </div>
      ))}
    </div>
  );
}

function MockupAttendance() {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {["T2", "T3", "T4", "T5", "T6"].map((d, i) => (
        <div key={i} className="text-center">
          <div className="text-[8px] text-slate-400 dark:text-slate-500 mb-1">{d}</div>
          <div className={cn(
            "size-6 rounded-full grid place-items-center text-[9px] font-bold mx-auto",
            i < 3 ? "bg-emerald-100 text-emerald-700 dark:text-emerald-300" : i === 3 ? "bg-amber-100 text-amber-700 dark:text-amber-300" : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500"
          )}>
            {i < 3 ? "✓" : i === 3 ? "!" : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function MockupAssignments() {
  return (
    <div className="space-y-1.5">
      {[
        { t: "Bài 5 — Chương 3", due: "2 ngày", c: "bg-amber-100 text-amber-700 dark:text-amber-300" },
        { t: "Đề kiểm tra giữa kỳ", due: "Đã nộp", c: "bg-emerald-100 text-emerald-700 dark:text-emerald-300" },
      ].map((a, i) => (
        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10">
          <FileText className="size-3 text-amber-600" strokeWidth={1.5} />
          <span className="text-[10px] font-medium text-slate-700 dark:text-slate-200 flex-1 truncate">{a.t}</span>
          <span className={cn("text-[8px] px-1.5 py-0.5 rounded-full font-medium", a.c)}>{a.due}</span>
        </div>
      ))}
    </div>
  );
}

function MockupFees() {
  return (
    <div className="rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 p-2.5">
      {[
        { n: "Nguyễn An", s: "1.500.000đ", paid: true },
        { n: "Trần Bảo", s: "1.200.000đ", paid: true },
        { n: "Lê Cường", s: "1.500.000đ", paid: false },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 py-1 border-b border-slate-50 last:border-0">
          <div className="size-4 rounded-full bg-violet-100" />
          <span className="text-[9px] text-slate-600 dark:text-slate-400 flex-1 truncate">{r.n}</span>
          <span className="text-[9px] font-medium text-slate-700 dark:text-slate-200">{r.s}</span>
          <div className={cn("size-1.5 rounded-full", r.paid ? "bg-emerald-500" : "bg-amber-500")} />
        </div>
      ))}
    </div>
  );
}

function MockupDashboard() {
  return (
    <div className="rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 p-2.5">
      <div className="h-1 w-12 rounded-full bg-slate-200 dark:bg-slate-800 mb-2" />
      <div className="flex items-end gap-1 h-12">
        {[40, 60, 35, 75, 50, 80, 65, 90].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-rose-500/20" style={{ height: `${h}%` }}>
            <div className="w-full rounded-t bg-rose-500" style={{ height: `${Math.min(h, 65)}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MockupNotifications() {
  return (
    <div className="space-y-1.5">
      {[
        { t: "Thông báo nghỉ lễ 2/9", to: "Tất cả phụ huynh", time: "2 phút trước" },
        { t: "Lịch thi cuối kỳ", to: "Khối 12", time: "1 giờ trước" },
      ].map((n, i) => (
        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10">
          <div className="size-6 rounded-lg bg-sky-100 grid place-items-center shrink-0">
            <Bell className="size-3 text-sky-600" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-medium text-slate-700 dark:text-slate-200 truncate">{n.t}</div>
            <div className="text-[8px] text-slate-400 dark:text-slate-500">{n.to} · {n.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

type FeatureCard = {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  desc: string;
  color: string;
  span: string;
  mockup: React.ReactNode;
};

const FEATURES: FeatureCard[] = [
  {
    icon: BookOpen,
    title: "Quản lý lớp học",
    desc: "Tạo lớp, xếp lịch, phân giáo viên — tất cả trong vài click. Quản lý nhiều cơ sở, nhiều ca học.",
    color: "text-blue-600",
    span: "md:col-span-8",
    mockup: <MockupClasses />,
  },
  {
    icon: ClipboardCheck,
    title: "Điểm danh 1 click",
    desc: "Trên điện thoại, ngay trong lớp. Theo dõi chuyên cần tự động.",
    color: "text-emerald-600",
    span: "md:col-span-4",
    mockup: <MockupAttendance />,
  },
  {
    icon: FileText,
    title: "Bài tập",
    desc: "Giao bài, chấm điểm, feedback. Theo dõi nộp bài.",
    color: "text-amber-600",
    span: "md:col-span-4",
    mockup: <MockupAssignments />,
  },
  {
    icon: CreditCard,
    title: "Học phí & Lương",
    desc: "Quản lý tài chính minh bạch. Thu chi theo tháng.",
    color: "text-violet-600",
    span: "md:col-span-4",
    mockup: <MockupFees />,
  },
  {
    icon: BarChart3,
    title: "Dashboard",
    desc: "Tổng quan real-time, báo cáo chi tiết.",
    color: "text-rose-600",
    span: "md:col-span-4",
    mockup: <MockupDashboard />,
  },
  {
    icon: Bell,
    title: "Thông báo thời gian thực",
    desc: "Gửi hàng loạt cho phụ huynh, học viên, giáo viên. Hẹn giờ và đính kèm file.",
    color: "text-sky-600",
    span: "md:col-span-8",
    mockup: <MockupNotifications />,
  },
];

export function LandingFeatures() {
  return (
    <section id="features" className="py-24 md:py-32 px-4 bg-white dark:bg-slate-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: EASE }}
            className="inline-flex px-3.5 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-100 text-[11px] uppercase tracking-[0.15em] font-medium text-emerald-700 dark:text-emerald-300 mb-4"
          >
            Tính năng
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: EASE }}
            className="font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
          >
            Mọi thứ bạn cần, không gì thừa
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5, ease: EASE }}
            className="mt-4 text-slate-500 dark:text-slate-400 max-w-xl mx-auto"
          >
            Bớt thao tác thủ công. Tập trung vào việc dạy.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-12 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: EASE }}
                whileHover={{ y: -6, transition: { duration: 0.3, ease: EASE } }}
                className={`${f.span} group p-1.5 rounded-[1.75rem] bg-black/[0.02] dark:bg-white/5 ring-1 ring-black/[0.04] dark:ring-white/10 transition-shadow duration-500 hover:shadow-[0_20px_60px_rgb(59,130,246,0.12)] dark:hover:shadow-[0_20px_60px_rgb(99,102,241,0.18)]`}
              >
                <div className="rounded-[calc(1.75rem-6px)] bg-white dark:bg-slate-900 h-full overflow-hidden shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] flex flex-col">
                  <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border-b border-slate-100 dark:border-slate-800">
                    {f.mockup}
                  </div>
                  <div className="p-6 flex-1">
                    <Icon className={`size-7 ${f.color} mb-3 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`} strokeWidth={1.5} />
                    <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">{f.title}</h3>
                    <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-[13px] leading-relaxed max-w-md">{f.desc}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
